import { useState } from 'react';
import StoreLobby from './StoreLobby';
import RoverSalesRoom from './RoverSalesRoom';
import OutfittingRoom from './OutfittingRoom';
import ResourceSalesRoom from './ResourceSalesRoom';
import type { RoverType } from '@/components/RoverPlacementModal/RoverPlacementModal';
import './StoreModal.css';

export type StoreRoom = 
  | 'lobby'
  | 'rover-sales'
  | 'resource-sales'
  | 'outfit-food'
  | 'outfit-energy'
  | 'outfit-mining'
  | 'outfit-water'
  | 'outfit-helium3';

export interface PurchasedRover {
  id: string;
  isOutfitted: boolean;
  outfitType?: string;
}

interface StoreModalProps {
  isOpen: boolean;
  playerMoney: number;
  playerResources: Record<string, number>;
  storeInventory: {
    availableRovers: number;
    metalsInStore: number;
    energyInStore: number;
    roversNextTurn: number;
  };
  onClose: () => void;
  onPurchaseRover: () => Promise<void>;
  onOutfitRover: (roverType: RoverType) => Promise<void>;
  onSellResources: (resources: Record<string, number>) => Promise<void>;
}

export default function StoreModal({
  isOpen,
  playerMoney,
  playerResources,
  storeInventory,
  onClose,
  onPurchaseRover,
  onOutfitRover,
  onSellResources,
}: StoreModalProps) {
  const [currentRoom, setCurrentRoom] = useState<StoreRoom>('lobby');
  const [purchasedRover, setPurchasedRover] = useState<PurchasedRover | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  if (!isOpen) return null;

  const navigateToRoom = (room: StoreRoom) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentRoom(room);
      setIsTransitioning(false);
    }, 300);
  };

  const handlePurchaseRover = async () => {
    try {
      await onPurchaseRover();
      // Create a purchased rover that follows the player
      const rover: PurchasedRover = {
        id: `rover-${Date.now()}`,
        isOutfitted: false,
      };
      setPurchasedRover(rover);
    } catch (error) {
      console.error('Failed to purchase rover:', error);
    }
  };

  const handleOutfitRover = async (roverType: RoverType) => {
    if (!purchasedRover) return;
    
    try {
      await onOutfitRover(roverType);
      // Mark rover as outfitted
      setPurchasedRover({
        ...purchasedRover,
        isOutfitted: true,
        outfitType: roverType.id,
      });
    } catch (error) {
      console.error('Failed to outfit rover:', error);
    }
  };

  const handleExitStore = () => {
    // If player has an outfitted rover, they can exit
    // If they have an unoutfitted rover, warn them
    if (purchasedRover && !purchasedRover.isOutfitted) {
      const confirm = window.confirm(
        'Your rover is not outfitted yet! Are you sure you want to leave?'
      );
      if (!confirm) return;
    }
    
    setPurchasedRover(null);
    setCurrentRoom('lobby');
    onClose();
  };

  return (
    <div className="store-modal-overlay">
      <div className="store-modal">
        {/* Header */}
        <div className="store-header">
          <div className="store-title">
            <span className="store-icon">🏪</span>
            R.O.V.E.R. DEPOT - LUNAR BASE
          </div>
          <div className="store-status">
            <span className="money">💰 {playerMoney} CT</span>
            {purchasedRover && (
              <span className="rover-indicator">
                🤖 Rover {purchasedRover.isOutfitted ? '(Outfitted)' : '(Blank)'}
              </span>
            )}
          </div>
        </div>

        {/* Room Content */}
        <div className={`store-content ${isTransitioning ? 'transitioning' : ''}`}>
          {isTransitioning && (
            <div className="room-transition">
              <div className="loading-spinner">⏳</div>
              <div>Entering room...</div>
            </div>
          )}

          {!isTransitioning && currentRoom === 'lobby' && (
            <StoreLobby
              availableRovers={storeInventory.availableRovers}
              playerMoney={playerMoney}
              hasPurchasedRover={purchasedRover !== null}
              onNavigate={navigateToRoom}
              onExit={handleExitStore}
            />
          )}

          {!isTransitioning && currentRoom === 'rover-sales' && (
            <RoverSalesRoom
              availableRovers={storeInventory.availableRovers}
              metalsInStore={storeInventory.metalsInStore}
              energyInStore={storeInventory.energyInStore}
              roversNextTurn={storeInventory.roversNextTurn}
              playerMoney={playerMoney}
              hasPurchasedRover={purchasedRover !== null}
              onPurchase={handlePurchaseRover}
              onBack={() => navigateToRoom('lobby')}
            />
          )}

          {!isTransitioning && currentRoom === 'resource-sales' && (
            <ResourceSalesRoom
              playerResources={playerResources}
              playerMoney={playerMoney}
              onSell={onSellResources}
              onBack={() => navigateToRoom('lobby')}
            />
          )}

          {!isTransitioning && currentRoom.startsWith('outfit-') && (
            <OutfittingRoom
              roomType={currentRoom.replace('outfit-', '') as any}
              playerMoney={playerMoney}
              hasPurchasedRover={purchasedRover !== null}
              isRoverOutfitted={purchasedRover?.isOutfitted || false}
              onOutfit={handleOutfitRover}
              onBack={() => navigateToRoom('lobby')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

